import { useRef } from 'react';
import {
  DietarySelection,
  parseAllergyInput,
  syncAllergyRestrictions,
  toggleDietarySelection,
} from '../utils/dietaryOptions';

type UseDietarySelection = {
  /** Check/uncheck one of COMMON_ALLERGIES, syncing implied restrictions. */
  toggleAllergy: (allergy: string) => void;
  /** Add comma-separated free text; returns whether anything was added. */
  addAllergiesFromText: (text: string) => boolean;
  removeAllergy: (allergy: string) => void;
  /** "No food allergies" / "Cancel" — drops the implied tags too. */
  clearAllergies: () => void;
  toggleDietaryRestriction: (restriction: string) => void;
  /** Replace the restriction list (preset chips, section clears). */
  setDietaryRestrictions: (restrictions: string[]) => void;
};

/**
 * Editing logic shared by the onboarding DietaryRestrictionsStep and the
 * profile DietaryPreference component, which display the same three sections
 * over the same value.
 *
 * It exists to keep the allergy → restriction sync in one place: the two
 * surfaces previously carried copies of it, and the copies encoded a bug
 * where unchecking the Wheat allergy silently removed a Gluten-Free
 * restriction the user had chosen themselves. See syncAllergyRestrictions
 * for the rules.
 */
export const useDietarySelection = (
  value: DietarySelection,
  onChange: (value: DietarySelection) => void
): UseDietarySelection => {
  // Restriction tags this session auto-added from an allergy, and may
  // therefore auto-remove. A ref, not state: it never affects the render,
  // and handlers must see writes from an earlier handler in the same tick.
  const autoAddedRef = useRef<string[]>([]);

  const setAllergies = (allergies: string[]) => {
    const synced = syncAllergyRestrictions(value, allergies, autoAddedRef.current);
    autoAddedRef.current = synced.autoAddedTags;
    onChange(synced.value);
  };

  const setDietaryRestrictions = (dietaryRestrictions: string[]) => {
    // Anything no longer selected is no longer ours to remove later.
    autoAddedRef.current = autoAddedRef.current.filter(tag =>
      dietaryRestrictions.includes(tag)
    );
    onChange({ ...value, dietaryRestrictions });
  };

  return {
    toggleAllergy: (allergy: string) =>
      setAllergies(
        value.allergies.includes(allergy)
          ? value.allergies.filter(a => a !== allergy)
          : [...value.allergies, allergy]
      ),

    addAllergiesFromText: (text: string) => {
      const added = parseAllergyInput(text, value.allergies);
      if (added.length === 0) return false;
      setAllergies([...value.allergies, ...added]);
      return true;
    },

    removeAllergy: (allergy: string) =>
      setAllergies(value.allergies.filter(a => a !== allergy)),

    clearAllergies: () => setAllergies([]),

    toggleDietaryRestriction: (restriction: string) => {
      // Checking or unchecking by hand makes the tag the user's own, so a
      // later allergy uncheck must not undo it.
      autoAddedRef.current = autoAddedRef.current.filter(t => t !== restriction);
      onChange({
        ...value,
        dietaryRestrictions: toggleDietarySelection(value.dietaryRestrictions, restriction),
      });
    },

    setDietaryRestrictions,
  };
};
